import { ChevronLeftIcon } from "Icons/ChevronLeft";
import { ChevronRightIcon } from "Icons/ChevronRight";

import {
  MutableRefObject,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { css } from "@emotion/react";

import { Button } from "components/shared/library/Button";

const style = {
  slidingWrapper: css({
    position: "relative",
    margin: -4,
  }),
  slidingArea: css({
    overflow: "scroll",
    padding: 4,
    WebkitOverflowScrolling: "touch",
    "&::-webkit-scrollbar": {
      width: "0px",
      display: "none",
    },
    "&:hover::-webkit-scrollbar": {
      display: "none",
    },
  }),
  button: css({
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 2,
  }),
  left: css({
    left: 11,
  }),
  right: css({
    right: 11,
  }),
};

export const HorizontalSlider = ({
  children,
  size = "medium",
  externalRef,
  showButtons = true,
}: {
  children: ReactNode;
  size?: "small" | "medium";
  externalRef?: MutableRefObject<HTMLDivElement | null>;
  showButtons?: boolean;
}) => {
  const [isScrolledLeft, setIsScrolledLeft] = useState(true);
  const [isScrolledRight, setIsScrolledRight] = useState(false);
  const scrollArea = useRef<HTMLDivElement>(null);
  const sliderRef = externalRef || scrollArea;
  const scrollThreshold = useMemo(() => 16, []);

  // check if fully left or right
  useEffect(() => {
    const checkScrollPosition = () => {
      if (sliderRef.current) {
        setIsScrolledLeft(sliderRef.current.scrollLeft < scrollThreshold);
        setIsScrolledRight(
          sliderRef.current.scrollWidth -
            sliderRef.current.scrollLeft -
            sliderRef.current.clientWidth <
            scrollThreshold
        );
      }
    };

    checkScrollPosition();
    const tempScrollArea = sliderRef.current;
    tempScrollArea?.addEventListener("scroll", checkScrollPosition);
    return () => {
      tempScrollArea?.removeEventListener("scroll", checkScrollPosition);
    };
  }, [scrollThreshold, sliderRef]);

  const handleClick = useCallback(
    (direction: "left" | "right") => {
      if (sliderRef.current) {
        const { scrollWidth, clientWidth, scrollLeft } = sliderRef.current;
        const fullRight = scrollWidth - clientWidth;
        const middlePoint = fullRight / 2;
        const leftLogic = scrollLeft <= middlePoint ? 0 : middlePoint;
        const rightLogic = scrollLeft < middlePoint ? middlePoint : fullRight;
        sliderRef.current.scrollTo({
          left: direction === "left" ? leftLogic : rightLogic,
          behavior: "smooth",
        });
      }
    },
    [sliderRef]
  );

  return (
    <div css={style.slidingWrapper}>
      {!isScrolledLeft && showButtons && (
        <Button
          ariaLabel="Scroll left"
          customCss={{ wrapper: [style.button, style.left] }}
          IconLeft={ChevronLeftIcon}
          onClick={() => handleClick("left")}
          size={size}
          color="purple"
        />
      )}
      <div
        css={style.slidingArea}
        ref={sliderRef}
        data-testid="quick-start-scroll-element"
      >
        {children}
      </div>
      {!isScrolledRight && showButtons && (
        <Button
          ariaLabel="Scroll right"
          customCss={{ wrapper: [style.button, style.right] }}
          IconRight={ChevronRightIcon}
          onClick={() => handleClick("right")}
          size={size}
          color="purple"
        />
      )}
    </div>
  );
};
